import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { t } from '@lingui/macro'

import { useLazyReadVitaleCardQuery, useSelectVitaleCardMutation, useStartCardMonitoring } from '@/core/services/dmp-connect-js2'
import { VitaleCardStatusEnum } from '@/core/types/dmp-connect'
import { useAppSelector } from '@/redux/rootReducer'
import {
  selectVitaleCard as selectVitaleCardSelector,
  selectVitaleCardReaderName,
  setVitaleCard,
  setVitaleCardReaderName,
} from '@/redux/reducers/dmp-connect.reducer'
import { getApiErrorMessage } from '@/utils/errors'

const formSchema = yup.object({
  cardName: yup.string().required().min(1),
})

export type DMPConnectVitaleCardFormData = yup.InferType<typeof formSchema>

export function useDMPConnectVitaleCardForm() {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const initialVitaleCardReaderName = useAppSelector(selectVitaleCardReaderName)
  const vitaleCard = useAppSelector(selectVitaleCardSelector)

  const [selectVitaleCard, { isLoading: isSelectingCard }] = useSelectVitaleCardMutation()
  const [readVitaleCard, { cardSerialNumber, vitaleCardPatients, isReadingCard }] = useLazyReadVitaleCardQuery({
    selectFromResult: ({ currentData, isFetching }) => ({
      cardSerialNumber: currentData?.s_serialNumber,
      vitaleCardPatients: currentData?.Patients,
      isReadingCard: isFetching,
    }),
  })
  const { isMonitored, startMonitoring, stopMonitoring, vitaleCardMonitoringData } = useStartCardMonitoring('vitale')

  const rhForm = useForm<DMPConnectVitaleCardFormData>({ defaultValues: { cardName: initialVitaleCardReaderName ?? '' }, resolver: yupResolver(formSchema) })
  const { reset } = rhForm

  const newCardInserted = vitaleCardMonitoringData?.i_cardStatus === VitaleCardStatusEnum.NEW_CARD_INSERTED
  const noCardInserted = vitaleCardMonitoringData?.i_cardStatus === VitaleCardStatusEnum.NO_CARD_INSERTED
  const isSameCard = cardSerialNumber && vitaleCardMonitoringData?.s_serial.endsWith(cardSerialNumber)

  const submitHandler: SubmitHandler<DMPConnectVitaleCardFormData> = async (data) => {
    if (newCardInserted) {
      try {
        await stopMonitoring()
      } catch {}
    }
    try {
      const { s_status } = await selectVitaleCard({ s_readerName: data.cardName }).unwrap()
      if (s_status === 'ERROR') throw new Error('Cannot get vitale card')
      dispatch(setVitaleCardReaderName(data.cardName))
      const vitaleCard = await readVitaleCard({}).unwrap()
      dispatch(setVitaleCard(vitaleCard))
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t`Read vitale card error`), { variant: 'error' })
    }
  }

  useEffect(() => {
    if (noCardInserted || newCardInserted) {
      reset()
    }
  }, [noCardInserted, newCardInserted, reset])

  useEffect(() => {
    if (!!isMonitored || !vitaleCard) return
    startMonitoring(10)
  }, [isMonitored, startMonitoring, vitaleCard])

  return {
    isSameCard,
    isSubmitting: isSelectingCard || isReadingCard,
    newCardInserted,
    noCardInserted,
    rhForm,
    submitHandler,
    vitaleCardPatients,
  }
}
